import {
  initKeycloakIfAuthenticated,
  KeycloakRoles,
} from '@coverright/shared/keycloak';
import { initAnalytics } from '@coverright/shared/analytics';
import { environment } from './environments/environment';
import {
  AgentAssignedToClientDocument,
  AgentAssignedToClientQuery, AgentAssignedToClientQueryVariables, UserProfileDocument, UserProfileQuery } from '@coverright/data-access/enrollment';
import {
  enrollmentApolloClient,
  mainBackendApolloClient,
} from '@coverright/data-access/apollo-clients';
import {
  MedicareQuoteDocument,
  MedicareQuoteQuery,
  MedicareQuoteQueryVariables,
} from '@coverright/data-access/medicare';
import { fillStoredUserFromUrlParams } from '@coverright/features/fill-stored-user-from-url-params';
import { getRoute } from './app/app/router';
import { initBrand } from '@coverright/shared/brand';

// Anonymous visitors almost always land on these routes first; don't block
// their first paint on the keycloak silent-SSO round trip, and don't chase a
// visible fallback redirect if the silent check can't confirm a session —
// these pages don't need to know whether the visitor is logged in.
const PRE_AUTH_ENTRY_PATHS = new Set([
  '/',
  getRoute('zip'),
  getRoute('basicInfo'),
  getRoute('personalInfo'),
]);
export const isPreAuthEntryPage = PRE_AUTH_ENTRY_PATHS.has(window.location.pathname);

// posthog-js is dynamically imported (not the @posthog/react wrapper, which
// statically imports it as a top-level side effect) so it lands in its own
// lazy chunk instead of ~198KB of eager weight on these pre-auth pages.
// Kicked off immediately at module load, in parallel with everything else, so
// its flags have almost always loaded by the time a page needs them.
let posthogPromise: Promise<(typeof import('posthog-js'))['default']> | null = null;
const loadPosthog = () => {
  if (!posthogPromise) {
    posthogPromise = import('posthog-js').then(m => m.default);
  }
  return posthogPromise;
};

declare global {
  interface Window {
    posthog?: (typeof import('posthog-js'))['default'];
  }
}

// Landing pages on other domains (e.g. the top-medicare / top-retirement
// /get-started wizard) can't share a PostHog cookie with this app, so they pass
// their session/distinct id in the hash — same convention coverright.com reads.
// Captured synchronously, before the router can navigate the hash away.
const hashParams = new URLSearchParams(window.location.hash.substring(1));
const bootstrapDistinctId = hashParams.get('distinct_id') || undefined;
const bootstrapSessionId = hashParams.get('session_id') || undefined;

async function initPosthog() {
  if (!environment.production) return null;
  const posthog = await loadPosthog();
  posthog.init(environment.posthogProjectToken, {
    api_host: "https://us.i.posthog.com",
    defaults: "2026-01-30",
    bootstrap: {
      sessionID: bootstrapSessionId,
      distinctID: bootstrapDistinctId,
    },
  });
  // Exposed on window so flags can be inspected/overridden from the console
  // (posthog.featureFlags.override({...})), matching the marketplace/landing
  // posthog setups.
  window.posthog = posthog;
  return posthog;
}

const posthogReady = initPosthog();

// Resolves a PostHog feature-flag/experiment variant without pulling in
// @posthog/react's hooks. Settles to `undefined` (treat as control) if
// PostHog is uninitialized (non-prod) or its flags haven't loaded within
// `timeoutMs`, so a blocked/slow PostHog never hangs the caller.
export async function getFeatureFlagVariant(key: string, timeoutMs = 1500) {
  const posthog = await posthogReady;
  if (!posthog) return undefined;
  if (posthog.featureFlags.hasLoadedFlags) return posthog.getFeatureFlag(key);

  return new Promise<boolean | string | undefined>((resolve) => {
    let settled = false;
    const finish = (value: boolean | string | undefined) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    const unsubscribe = posthog.onFeatureFlags(() => finish(posthog.getFeatureFlag(key)));
    setTimeout(() => {
      unsubscribe();
      finish(undefined);
    }, timeoutMs);
  });
}

async function initKeycloakAndUserData() {
  const keycloak = await initKeycloakIfAuthenticated({
    // Point the silent-SSO check at a tiny static page instead of the current
    // route (the shared lib's default) — otherwise keycloak's hidden iframe
    // re-downloads and re-boots this entire app a second time on every load.
    silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
    // When the iframe-based check can't complete reliably (e.g. Chrome
    // Incognito blocks third-party iframe storage — a known keycloak-js
    // limitation), it normally falls back to a full top-level redirect
    // through the auth server to confirm the session. Skip that fallback
    // only on the pre-auth pages, where we don't care whether the visitor
    // is logged in — protected routes keep the fallback so an actually
    // logged-in user isn't bounced to login just because the iframe check
    // was inconclusive.
    silentCheckSsoFallback: !isPreAuthEntryPage,
  });

  const isAdmin = keycloak.hasRealmRole(KeycloakRoles.ROLE_ADMIN);

  if (isAdmin) {
    // eslint-disable-next-line no-restricted-globals
    const result  = confirm('You are logged in as admin. Sign out?')
    if (result) {
      keycloak.logout();
    } else {
      throw new Error('Wrong role');
    }
  }

  if (keycloak.authenticated) {
    const { data } = await enrollmentApolloClient.query<UserProfileQuery>({
      query: UserProfileDocument,
    });

    const userProfile = { ...data!.userProfile! };

    const quoteId = userProfile.quoteId

    const posthog = await posthogReady;
    if (posthog && posthog.get_distinct_id()) {
      posthog.identify(userProfile.clientId, {
        email: userProfile.email,
        name: userProfile.firstName + ' ' + userProfile.lastName,
        profileId: userProfile?.profileId,
      });
    }

    const promises: any[] = [];

    // get quote
    promises.push(
      mainBackendApolloClient.query<MedicareQuoteQuery, MedicareQuoteQueryVariables>({
        query: MedicareQuoteDocument,
        variables: { id: quoteId },
      })
    );

    promises.push(
      fetch(
        `${environment.userFlowRestUrl}/getState/${quoteId}`
      ).then((data) => {
        if (data.status === 200) {
          return data.json();
        }
      })
    );


    // get agent
    if (data.userProfile?.clientId) {
      promises.push(
        enrollmentApolloClient.query<
          AgentAssignedToClientQuery,
          AgentAssignedToClientQueryVariables
        >({
          query: AgentAssignedToClientDocument,
          variables: { clientId: userProfile.clientId },
        })
      );
    }

    await Promise.all(promises).then(([quoteResponse, appState, agent, partner]) => {
      if (!appState?.state) {
        sessionStorage.setItem('appState', JSON.stringify({}));
      } else {
        sessionStorage.setItem('appState', JSON.stringify(appState.state));
      }
    });
  }

  return keycloak;
}

// Kicked off immediately at module load so the silent-SSO round trip starts
// as early as possible; callers decide whether to await it before rendering.
export const keycloakReady = initKeycloakAndUserData();

export async function initEarly() {
  initAnalytics('ai-consumer');

  await Promise.all([
    fillStoredUserFromUrlParams(mainBackendApolloClient, window.location.origin + '/zip'),
    initBrand(environment.brandApiUrl),
  ]);
}
